import {
  type AttachmentView as Attachment, type ConversationView, MAX_ATTACHMENT_BYTES, MAX_TEXT_LENGTH, type MessageView,
  type SendInput, type SendPolicy,
} from '@hilo/contracts';
import { useMutation, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import {
  type ClipboardEvent, type DragEvent, type FormEvent, type KeyboardEvent, useCallback, useEffect, useRef, useState, useSyncExternalStore,
} from 'react';
import { api, ApiError, uploadFile } from '../../lib/api';
import { CHANNEL_LABEL, errorText, isUncertain, policyReason } from '../../lib/copy';
import {
  clearDraft, type Draft, type DraftAttachment, getDraft, markUncertain, renewDraftId, setDraftAttachment, setDraftText,
  subscribeDraft, updateDraftAttachment,
} from '../../lib/drafts';
import { refreshConversation } from '../../lib/queries';
import { useCurrentUser } from '../../lib/session';
import { formatRemaining } from '../../lib/time';
import { IconAlert, IconClip, IconClock, IconHand, IconLock, IconSend, IconTemplate } from '../../ui/icons';
import { formatBytes } from './AttachmentView';
import { Select } from '../../ui/Select';
import { useNow } from '../../ui/useNow';
import { draftEvents } from './draftEvents';
import { contactName } from './names';
import styles from './ReplyComposer.module.css';

const UNCERTAIN_TEXT = 'No sabemos si se envió: se perdió la conexión antes de la respuesta. Pulsa Enviar otra vez; si ya había llegado, Hilo no lo duplicará.';

/** Tipos que Hilo acepta subir (el servidor decide por el contenido; esto solo filtra el selector). */
const ACCEPT = 'image/jpeg,image/png,audio/*,video/mp4,video/3gpp,application/pdf,text/plain,.docx,.xlsx';

function guessKind(file: File): DraftAttachment['kind'] {
  if (file.type.startsWith('image/')) return 'IMAGE';
  if (file.type.startsWith('audio/')) return 'AUDIO';
  if (file.type.startsWith('video/')) return 'VIDEO';
  return 'DOCUMENT';
}

/** Borrador de plantilla: su propio clientRequestId, separado del texto libre. */
const templateSlot = (conversationId: string) => `${conversationId}#plantilla`;

/**
 * Borrador vigente de `slot`, siempre desde el almacén: una subida que termina después de salir y volver
 * a la conversación se ve en el editor montado ahora (revisión de Codex de los adjuntos, P2 4).
 */
function useDraft(slot: string): Draft {
  const subscribe = useCallback((notify: () => void) => subscribeDraft(slot, notify), [slot]);
  return useSyncExternalStore(subscribe, () => getDraft(slot));
}

export function ReplyComposer({ conversation, policy, mine, supervisor }: {
  conversation: ConversationView;
  policy: UseQueryResult<SendPolicy>;
  mine: boolean;
  supervisor: boolean;
}) {
  const client = useQueryClient();
  const user = useCurrentUser();
  const id = conversation.id;
  // El borrador vive en memoria por conversación y se recupera al volver a ella.
  const draft = useDraft(id);
  const templateDraft = useDraft(templateSlot(id));
  const [feedback, setFeedback] = useState<{ tone: 'error' | 'warning'; text: string } | null>(null);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  // «Copiar al editor» desde un mensaje FAILED/UNKNOWN del hilo: el texto llega por el almacén; aquí, el foco.
  useEffect(() => {
    const onReused = (event: Event) => {
      if ((event as CustomEvent<string>).detail !== id) return;
      textarea.current?.focus();
    };
    draftEvents.addEventListener('reused', onReused);
    return () => draftEvents.removeEventListener('reused', onReused);
  }, [id]);

  const send = useMutation({
    mutationFn: (input: { slot: string; body: SendInput }) =>
      api<MessageView>('POST', `/conversations/${id}/messages`, { body: input.body }),
    onSuccess: (_message, input) => {
      clearDraft(input.slot);
      setFeedback(null);
      // El mensaje aparece cuando lo devuelve el servidor, nunca por optimismo local.
      void refreshConversation(client, id);
      textarea.current?.focus();
    },
    onError: (error, input) => {
      if (isUncertain(error)) {
        markUncertain(input.slot);
        setFeedback({ tone: 'warning', text: UNCERTAIN_TEXT });
        return;
      }
      if (error instanceof ApiError && error.code === 'IDEMPOTENCY_KEY_REUSED') {
        // El ID ya se usó con otro contenido: el texto actual es un envío nuevo.
        renewDraftId(input.slot);
      }
      setFeedback({ tone: 'error', text: errorText(error) });
      void refreshConversation(client, id);
    },
  });

  /** Adjunta un archivo al borrador y lo sube ya (WhatsApp: uno por mensaje; uno nuevo sustituye al anterior). */
  const attach = (file: File) => {
    setFeedback(null);
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setFeedback({ tone: 'error', text: 'El archivo supera los 25 MiB.' });
      return;
    }
    const localId = crypto.randomUUID();
    const kind = guessKind(file);
    setDraftAttachment(id, {
      localId, name: file.name, size: file.size, kind, previewUrl: kind === 'IMAGE' ? URL.createObjectURL(file) : null,
      status: 'uploading', progress: 0, attachmentId: null, error: null,
    });
    // Actualiza el almacén, no este componente: si se sale y se vuelve, lo ve el editor de ese momento.
    uploadFile<Attachment>(`/conversations/${id}/attachments`, file, (progress) => {
      updateDraftAttachment(id, localId, { progress });
    }).then((uploaded) => {
      updateDraftAttachment(id, localId, { status: 'ready', progress: 1, attachmentId: uploaded.id, kind: uploaded.kind, name: uploaded.name ?? file.name });
    }, (error: unknown) => {
      updateDraftAttachment(id, localId, { status: 'error', error: errorText(error) });
    });
  };

  const removeAttachment = () => {
    setDraftAttachment(id, null);
    textarea.current?.focus();
  };

  const onPaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const file = [...event.clipboardData.files][0];
    if (file && mediaAllowed) {
      event.preventDefault();
      attach(file);
    }
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    const file = [...event.dataTransfer.files][0];
    if (file && mediaAllowed) attach(file);
  };

  const data = policy.data;
  const contactLabel = contactName(conversation);
  const now = useNow();
  const gate = assignmentGate(conversation, mine, supervisor);
  const blocked = gate ?? policyGate(data, policy.isError, conversation.channel);
  const textAllowed = blocked === null && data?.allowed === true;
  // Ventana cerrada: el texto libre no, pero sí una plantilla aprobada del catálogo de la conexión.
  const templates = gate === null && data && !data.allowed && data.reason === 'WINDOW_CLOSED' ? data.templates : [];
  const mediaAllowed = textAllowed && !!data?.allowedKinds.some((kind) => kind !== 'TEXT');
  const text = draft.text;
  const trimmed = text.trim();
  const tooLong = text.length > MAX_TEXT_LENGTH;
  const attachment = draft.attachment;
  const attachmentReady = attachment?.status === 'ready' && attachment.attachmentId !== null;
  // Sin pie de texto: el audio en WhatsApp y cualquier archivo en Messenger e Instagram. El texto va en otro
  // mensaje (el servidor lo rechaza igual: AUDIO_WITHOUT_CAPTION, MEDIA_CAPTION_UNSUPPORTED).
  const noCaption = attachment ? attachment.kind === 'AUDIO' || conversation.channel !== 'WHATSAPP' : false;
  const canSend = !send.isPending && !tooLong && (attachment ? attachmentReady && !(noCaption && trimmed.length > 0) : trimmed.length > 0);

  const submitText = (event?: FormEvent) => {
    event?.preventDefault();
    if (!textAllowed || !canSend) return;
    const attachmentIds = attachmentReady && attachment.attachmentId ? [attachment.attachmentId] : [];
    send.mutate({ slot: id, body: { clientRequestId: draft.clientRequestId, text: attachment ? text.trim() : text, attachmentIds } });
  };

  const submitTemplate = () => {
    const [name, language] = templateDraft.text.split('|');
    if (!name || !language || send.isPending) return;
    send.mutate({
      slot: templateSlot(id),
      body: { clientRequestId: templateDraft.clientRequestId, text: '', attachmentIds: [], template: { name, language, parameters: [] } },
    });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) submitText();
  };

  return (
    <form className={styles.composer} onSubmit={submitText} aria-label="Responder al cliente">
      {blocked && (
        <p className={styles.blocked} role="status">
          <span className={styles.blockedIcon} aria-hidden="true">{gate ? <IconHand /> : <IconLock />}</span>
          {blocked}
        </p>
      )}
      {feedback && (
        <p className={styles[feedback.tone]} role="alert"><IconAlert />{feedback.text}</p>
      )}

      {templates.length > 0 && (
        <div className={styles.card}>
          <div className={styles.templates}>
            <span className={styles.templateIcon} aria-hidden="true"><IconTemplate /></span>
            <div className={styles.templateField}>
              <Select label="Plantilla aprobada" placeholder="Elige una plantilla" value={templateDraft.text}
                onChange={(value) => setDraftText(templateSlot(id), value)}
                options={templates.map((template) => ({
                  value: `${template.name}|${template.language}`, label: `${template.name} · ${template.language}`,
                }))} />
            </div>
            <button type="button" className={`btn btn-primary ${styles.send}`} disabled={!templateDraft.text || send.isPending} onClick={submitTemplate}>
              <IconSend />
              {send.isPending ? 'Enviando…' : 'Enviar plantilla'}
            </button>
          </div>
        </div>
      )}

      {textAllowed && (
        <div className={styles.card} data-dragging={dragging || undefined}
          onDragOver={(event) => { if (mediaAllowed && event.dataTransfer.types.includes('Files')) { event.preventDefault(); setDragging(true); } }}
          onDragLeave={() => setDragging(false)} onDrop={onDrop}>
          {dragging && <div className={styles.dropHint} aria-hidden="true">Suelta el archivo para adjuntarlo</div>}
          {attachment && (
            <div className={styles.attachment} data-status={attachment.status}>
              {attachment.previewUrl
                ? <img className={styles.thumb} src={attachment.previewUrl} alt="" />
                : <span className={styles.fileBadge} aria-hidden="true">{(attachment.name.split('.').pop() ?? 'doc').slice(0, 4).toUpperCase()}</span>}
              <span className={styles.attachmentText}>
                <span className={styles.attachmentName}>{attachment.name}</span>
                <span className={styles.attachmentMeta} role={attachment.status === 'error' ? 'alert' : undefined}>
                  {attachment.status === 'uploading' && `Subiendo… ${Math.round(attachment.progress * 100)} %`}
                  {attachment.status === 'ready' && `${formatBytes(attachment.size)} · listo para enviar`}
                  {attachment.status === 'error' && (attachment.error ?? 'No se pudo subir el archivo.')}
                </span>
                {attachment.status === 'uploading' && (
                  <span className={styles.progress} aria-hidden="true"><span style={{ width: `${attachment.progress * 100}%` }} /></span>
                )}
              </span>
              <button type="button" className={styles.remove} aria-label={`Quitar ${attachment.name}`} onClick={removeAttachment}>×</button>
            </div>
          )}
          <label className={styles.field}>
            <span className="visually-hidden">Respuesta</span>
            <textarea ref={textarea} className={styles.textarea} rows={3} value={text}
              disabled={noCaption && trimmed.length === 0}
              placeholder={noCaption
                ? `${conversation.channel === 'WHATSAPP' ? 'El audio se envía' : `${CHANNEL_LABEL[conversation.channel]} envía el archivo`} sin texto: escribe tu mensaje aparte`
                : attachment ? 'Añade un texto al archivo (opcional)' : `Escribe tu respuesta para ${contactLabel}`}
              aria-describedby={`ayuda-${id}`}
              onKeyDown={onKeyDown} onPaste={onPaste}
              onChange={(event) => setDraftText(id, event.target.value)} />
          </label>
          <div className={styles.toolbar}>
            <p id={`ayuda-${id}`} className={styles.help}>
              {data?.validUntil && (
                <span className={styles.window}><IconClock /> {conversation.channel !== 'WHATSAPP' && (data.replyWindow === 'HUMAN_AGENT' ? '7 días · HUMAN_AGENT · ' : '24 h · ')}Quedan {formatRemaining(data.validUntil, new Date(now))}{conversation.channel !== 'WHATSAPP' && ` · Vence ${new Date(data.validUntil).toLocaleString('es-SV', { timeZone: user.timezone })}`}</span>
              )}
              <span className={styles.keyHint}><kbd>Ctrl</kbd> + <kbd>Enter</kbd> envía</span>
              {text.length > MAX_TEXT_LENGTH - 500 && (
                <span className={tooLong ? styles.over : undefined}>{text.length.toLocaleString('es-SV')} / {MAX_TEXT_LENGTH.toLocaleString('es-SV')}</span>
              )}
            </p>
            {mediaAllowed && (
              <>
                <input ref={fileInput} type="file" accept={ACCEPT} hidden
                  onChange={(event) => { const file = event.target.files?.[0]; if (file) attach(file); event.target.value = ''; }} />
                <button type="button" className={styles.clip} aria-label="Adjuntar archivo" title="Adjuntar foto, audio, video o documento"
                  onClick={() => fileInput.current?.click()}>
                  <IconClip />
                </button>
              </>
            )}
            <button type="submit" className={`btn btn-primary ${styles.send}`} disabled={!canSend}>
              <IconSend />
              {send.isPending ? 'Enviando…' : 'Enviar'}
            </button>
          </div>
        </div>
      )}
    </form>
  );
}

/** Quién puede escribir: solo quien atiende una conversación no resuelta. El servidor lo vuelve a comprobar. */
function assignmentGate(conversation: ConversationView, mine: boolean, supervisor: boolean): string | null {
  if (conversation.state === 'RESOLVED') return 'La conversación está resuelta. Ábrela de nuevo para responder.';
  if (conversation.assigneeId === null) return 'Toma la conversación para responder.';
  if (mine) return null;
  const who = conversation.assigneeDisplayName ?? 'otra persona';
  return supervisor
    ? `La atiende ${who}. Para responder tú, asígnatela primero.`
    : `La atiende ${who}. Solo quien la atiende puede responder.`;
}

/** Qué permite el canal ahora (send-policy): ventana, identidad, destinatarios de prueba… */
function policyGate(policy: SendPolicy | undefined, failed: boolean, channel: ConversationView['channel']): string | null {
  if (!policy) return failed ? 'No se pudo comprobar si se puede responder. Se volverá a intentar en unos segundos.' : 'Comprobando si se puede responder…';
  if (!policy.allowed && policy.reason === 'WINDOW_CLOSED' && channel !== 'WHATSAPP') {
    return `Pasaron ${policy.replyWindow === 'HUMAN_AGENT' ? '7 días (HUMAN_AGENT)' : '24 horas'} desde el último mensaje del cliente. Espera a que vuelva a escribir para responder.`;
  }
  return policy.allowed ? null : policyReason(policy.reason);
}
